import {
  ContractDocusignSendHistoryDto,
  ContractListDto,
  LatestDocusignSendSummaryDto,
} from '../services/contracts/ContractService';
import { getContractTypeLabel, sortContractsForVtmeGroupOrder } from './contractGroupVtmeOrder';

export enum EDocusignSendHistoryStatus {
  Processing = 0,
  Success = 1,
  Error = 2,
}

/** A API .NET serializa enums como string ("Success"); o front também aceita número. */
export type DocusignSendStatusInput = number | string | undefined | null;

function parseDocusignSendStatusCode(
  status: DocusignSendStatusInput
): EDocusignSendHistoryStatus | null {
  if (status === null || status === undefined || status === '') {
    return null;
  }

  if (typeof status === 'number' && !Number.isNaN(status)) {
    if (status === EDocusignSendHistoryStatus.Processing) return EDocusignSendHistoryStatus.Processing;
    if (status === EDocusignSendHistoryStatus.Success) return EDocusignSendHistoryStatus.Success;
    if (status === EDocusignSendHistoryStatus.Error) return EDocusignSendHistoryStatus.Error;
  }

  const normalized = String(status).trim().toLowerCase();
  if (normalized === 'processing' || normalized === '0') {
    return EDocusignSendHistoryStatus.Processing;
  }
  if (normalized === 'success' || normalized === 'sucesso' || normalized === '1') {
    return EDocusignSendHistoryStatus.Success;
  }
  if (normalized === 'error' || normalized === 'erro' || normalized === '2') {
    return EDocusignSendHistoryStatus.Error;
  }

  return null;
}

export function normalizeDocusignSendStatus(
  status: DocusignSendStatusInput,
  isProcessing?: boolean
): EDocusignSendHistoryStatus {
  const parsed = parseDocusignSendStatusCode(status);
  if (
    parsed === EDocusignSendHistoryStatus.Success ||
    parsed === EDocusignSendHistoryStatus.Error
  ) {
    return parsed;
  }
  if (isProcessing || parsed === EDocusignSendHistoryStatus.Processing) {
    return EDocusignSendHistoryStatus.Processing;
  }
  return parsed ?? EDocusignSendHistoryStatus.Error;
}

export function getDocusignStatusLabel(
  status: DocusignSendStatusInput,
  isProcessing?: boolean
): string {
  const code = normalizeDocusignSendStatus(status, isProcessing);
  if (code === EDocusignSendHistoryStatus.Processing) return 'Processando';
  if (code === EDocusignSendHistoryStatus.Success) return 'Sucesso';
  return 'Erro';
}

export function normalizeLatestDocusignSendSummary(
  summary: LatestDocusignSendSummaryDto
): LatestDocusignSendSummaryDto {
  const status = normalizeDocusignSendStatus(summary.status, summary.isProcessing);
  const isProcessing = status === EDocusignSendHistoryStatus.Processing;
  return {
    ...summary,
    status,
    isProcessing,
    statusText: getDocusignStatusLabel(status, isProcessing),
  };
}

export function aggregateGroupLatestDocusignSend(
  contracts: ContractListDto[]
): LatestDocusignSendSummaryDto | undefined {
  const items = contracts
    .map((c) => c.latestDocusignSend)
    .filter((x): x is LatestDocusignSendSummaryDto => Boolean(x))
    .map(normalizeLatestDocusignSendSummary);

  if (!items.length) return undefined;

  if (items.some((i) => normalizeDocusignSendStatus(i.status, i.isProcessing) === EDocusignSendHistoryStatus.Processing)) {
    return items.find(
      (i) => normalizeDocusignSendStatus(i.status, i.isProcessing) === EDocusignSendHistoryStatus.Processing
    )!;
  }
  if (items.some((i) => normalizeDocusignSendStatus(i.status, i.isProcessing) === EDocusignSendHistoryStatus.Error)) {
    return items.find(
      (i) => normalizeDocusignSendStatus(i.status, i.isProcessing) === EDocusignSendHistoryStatus.Error
    )!;
  }
  if (items.some((i) => normalizeDocusignSendStatus(i.status, i.isProcessing) === EDocusignSendHistoryStatus.Success)) {
    return items.find(
      (i) => normalizeDocusignSendStatus(i.status, i.isProcessing) === EDocusignSendHistoryStatus.Success
    )!;
  }
  return items[0];
}

export type DocusignBadgeColor = 'grey' | 'blue' | 'green' | 'red' | 'yellow';

export type DocusignPerContractSendState = 'none' | 'processing' | 'success' | 'error';

export interface DocusignBadgeConfig {
  color: DocusignBadgeColor;
  text: 'Docusign';
  clickable: boolean;
  hoverMessage?: string;
  showDetailTooltip: boolean;
  detailSummary?: LatestDocusignSendSummaryDto;
}

const DOCUSIGN_SENT_SUCCESS_MESSAGE = 'Contrato enviado pelo Docusign';

export function getPerContractDocusignSendState(
  contract: ContractListDto
): DocusignPerContractSendState {
  const summary = contract.latestDocusignSend;
  if (!summary) return 'none';

  const status = normalizeDocusignSendStatus(summary.status, summary.isProcessing);
  if (status === EDocusignSendHistoryStatus.Processing) return 'processing';
  if (status === EDocusignSendHistoryStatus.Success) return 'success';
  return 'error';
}

export function getPerContractDocusignSendStateLabel(state: DocusignPerContractSendState): string {
  switch (state) {
    case 'none':
      return 'Não enviado';
    case 'processing':
      return 'Processando';
    case 'success':
      return 'Enviado com sucesso';
    case 'error':
      return 'Erro no envio';
    default:
      return '—';
  }
}

export function buildGroupDocusignPartialTooltip(contracts: ContractListDto[]): string {
  return sortContractsForVtmeGroupOrder(contracts)
    .map((contract) => {
      const label = getContractTypeLabel(contract);
      const state = getPerContractDocusignSendState(contract);
      return `${label}: ${getPerContractDocusignSendStateLabel(state)}`;
    })
    .join('\n');
}

function getSingleDocusignBadgeConfig(
  summary: LatestDocusignSendSummaryDto | undefined | null
): DocusignBadgeConfig {
  if (!summary) {
    return {
      color: 'grey',
      text: 'Docusign',
      clickable: true,
      hoverMessage: 'Enviar contrato pelo Docusign',
      showDetailTooltip: false,
    };
  }

  const status = normalizeDocusignSendStatus(summary.status, summary.isProcessing);

  if (status === EDocusignSendHistoryStatus.Processing) {
    return {
      color: 'blue',
      text: 'Docusign',
      clickable: false,
      showDetailTooltip: true,
      detailSummary: summary,
    };
  }
  if (status === EDocusignSendHistoryStatus.Success) {
    return {
      color: 'green',
      text: 'Docusign',
      clickable: true,
      hoverMessage: `${DOCUSIGN_SENT_SUCCESS_MESSAGE}. Clique para reenviar.`,
      showDetailTooltip: false,
    };
  }
  return {
    color: 'red',
    text: 'Docusign',
    clickable: true,
    hoverMessage: 'Erro ao enviar contrato pelo Docusign, reenviar.',
    showDetailTooltip: false,
  };
}

function getGroupDocusignBadgeConfig(groupContracts: ContractListDto[]): DocusignBadgeConfig {
  const ordered = sortContractsForVtmeGroupOrder(groupContracts);
  const states = ordered.map((contract) => getPerContractDocusignSendState(contract));

  if (states.every((state) => state === 'none')) {
    return {
      color: 'grey',
      text: 'Docusign',
      clickable: true,
      hoverMessage: 'Enviar contratos pelo Docusign',
      showDetailTooltip: false,
    };
  }

  if (states.some((state) => state === 'processing')) {
    const processingContract = ordered.find(
      (contract) => getPerContractDocusignSendState(contract) === 'processing'
    );
    const processingSummary = processingContract?.latestDocusignSend
      ? normalizeLatestDocusignSendSummary(processingContract.latestDocusignSend)
      : undefined;
    const hasPendingOrError = states.some((state) => state === 'none' || state === 'error');

    return {
      color: hasPendingOrError ? 'yellow' : 'blue',
      text: 'Docusign',
      clickable: true,
      hoverMessage: hasPendingOrError
        ? buildGroupDocusignPartialTooltip(ordered)
        : 'Envio em andamento. Você ainda pode abrir para acompanhar ou reenviar pendentes.',
      showDetailTooltip: Boolean(processingSummary),
      detailSummary: processingSummary,
    };
  }

  const successCount = states.filter((state) => state === 'success').length;
  const hasPendingOrError = states.some((state) => state === 'none' || state === 'error');

  if (successCount === ordered.length) {
    return {
      color: 'green',
      text: 'Docusign',
      clickable: true,
      hoverMessage: `${DOCUSIGN_SENT_SUCCESS_MESSAGE}. Clique para reenviar.`,
      showDetailTooltip: false,
    };
  }

  if (successCount === 1 && hasPendingOrError) {
    return {
      color: 'yellow',
      text: 'Docusign',
      clickable: true,
      hoverMessage: buildGroupDocusignPartialTooltip(ordered),
      showDetailTooltip: false,
    };
  }

  if (states.every((state) => state === 'error')) {
    return {
      color: 'red',
      text: 'Docusign',
      clickable: true,
      hoverMessage: buildGroupDocusignPartialTooltip(ordered),
      showDetailTooltip: false,
    };
  }

  return {
    color: 'red',
    text: 'Docusign',
    clickable: true,
    hoverMessage: buildGroupDocusignPartialTooltip(ordered),
    showDetailTooltip: false,
  };
}

export function getDocusignBadgeConfig(
  contract: ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] }
): DocusignBadgeConfig {
  const grouped = contract.groupContracts;
  if (grouped?.length) {
    return getGroupDocusignBadgeConfig(grouped);
  }

  const summary = contract.latestDocusignSend
    ? normalizeLatestDocusignSendSummary(contract.latestDocusignSend)
    : undefined;

  return getSingleDocusignBadgeConfig(summary);
}

export type DocusignVisualColor = 'grey' | 'blue' | 'green' | 'red';

export function getDocusignVisualColor(
  summary: LatestDocusignSendSummaryDto | undefined | null
): DocusignVisualColor {
  if (!summary) return 'grey';
  const status = normalizeDocusignSendStatus(summary.status, summary.isProcessing);
  if (status === EDocusignSendHistoryStatus.Processing) return 'blue';
  if (status === EDocusignSendHistoryStatus.Success) return 'green';
  return 'red';
}

/** Ícone de histórico: cinza sem envios; azul com histórico (igual ao ícone de histórico de status). */
export function getDocusignHistoryIconColor(
  summary: LatestDocusignSendSummaryDto | undefined | null
): 'grey' | 'blue' {
  return summary ? 'blue' : 'grey';
}

/** Ícone no contrato pai do grupo: azul se qualquer contrato do grupo tiver envio DocuSign. */
export function resolveDocusignHistoryIconColor(
  latestSend: LatestDocusignSendSummaryDto | undefined | null,
  groupContracts?: ContractListDto[]
): 'grey' | 'blue' {
  if (groupContracts?.length) {
    const hasAnyInGroup = groupContracts.some((member) => Boolean(member.latestDocusignSend));
    if (hasAnyInGroup) return 'blue';
  }

  return latestSend ? 'blue' : 'grey';
}

export function buildOptimisticDocusignProcessingSummary(params: {
  recipientName: string;
  recipientPhone: string;
  recipientEmail?: string | null;
  recipientContactType: number;
  recipientContactTypeText?: string;
  requestedByUserName?: string | null;
}): LatestDocusignSendSummaryDto {
  return normalizeLatestDocusignSendSummary({
    historyId: '',
    status: EDocusignSendHistoryStatus.Processing,
    statusText: 'Processando',
    isProcessing: true,
    requestedAt: new Date().toISOString(),
    recipientContactType: params.recipientContactType,
    recipientContactTypeText: params.recipientContactTypeText ?? 'Contato',
    recipientName: params.recipientName,
    recipientPhone: params.recipientPhone,
    recipientEmail: params.recipientEmail ?? null,
    requestedByUserName: params.requestedByUserName ?? null,
  });
}

export function applyDocusignSendResultToSummary(
  summary: LatestDocusignSendSummaryDto,
  result: 'success' | 'error'
): LatestDocusignSendSummaryDto {
  const status =
    result === 'success'
      ? EDocusignSendHistoryStatus.Success
      : EDocusignSendHistoryStatus.Error;
  return normalizeLatestDocusignSendSummary({
    ...summary,
    status,
    isProcessing: false,
    finishedAt: new Date().toISOString(),
  });
}

export function mapHistoryItemToLatestSummary(
  item: ContractDocusignSendHistoryDto
): LatestDocusignSendSummaryDto {
  const status = normalizeDocusignSendStatus(item.status, item.isProcessing);
  return {
    historyId: item.id,
    status,
    statusText: getDocusignStatusLabel(status, item.isProcessing),
    isProcessing: item.isProcessing,
    requestedAt: item.requestedAt,
    finishedAt: item.finishedAt,
    recipientContactType: item.recipientContactType,
    recipientContactTypeText: item.recipientContactTypeText,
    recipientName: item.recipientName,
    recipientPhone: item.recipientPhone,
    recipientEmail: item.recipientEmail,
    docusignAccountEmail: item.docusignAccountEmail,
    docusignContractLink: item.docusignContractLink,
    requestedByUserName: item.requestedByUserName,
  };
}

export function hasPriorDocusignSend(summary: LatestDocusignSendSummaryDto | undefined | null): boolean {
  if (!summary) return false;
  const status = normalizeDocusignSendStatus(summary.status, summary.isProcessing);
  return (
    summary.isProcessing ||
    status === EDocusignSendHistoryStatus.Processing ||
    status === EDocusignSendHistoryStatus.Success ||
    status === EDocusignSendHistoryStatus.Error
  );
}

export function resolveLatestDocusignSendFromContract(
  contract: ContractListDto
): LatestDocusignSendSummaryDto | undefined {
  const grouped = (contract as ContractListDto & { groupContracts?: ContractListDto[] }).groupContracts;
  const raw = contract.latestDocusignSend ?? (grouped?.length ? aggregateGroupLatestDocusignSend(grouped) : undefined);
  return raw ? normalizeLatestDocusignSendSummary(raw) : undefined;
}

export function formatDocusignDateTime(dateString?: string | null): string {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  } catch {
    return dateString;
  }
}

export function getDocusignSummaryLines(summary: LatestDocusignSendSummaryDto): string[] {
  const statusLabel = getDocusignStatusLabel(summary.status, summary.isProcessing);
  const lines = [
    `Status: ${statusLabel}`,
    `Destinatário (${summary.recipientContactTypeText}): ${summary.recipientName}`,
    `Telefone: ${summary.recipientPhone}`,
  ];
  if (summary.recipientEmail) lines.push(`E-mail: ${summary.recipientEmail}`);
  lines.push(`Solicitado em: ${formatDocusignDateTime(summary.requestedAt)}`);
  if (summary.finishedAt) {
    lines.push(`Finalizado em: ${formatDocusignDateTime(summary.finishedAt)}`);
  }
  if (summary.docusignAccountEmail) {
    lines.push(`Conta DocuSign: ${summary.docusignAccountEmail}`);
  }
  if (summary.requestedByUserName) {
    lines.push(`Solicitante: ${summary.requestedByUserName}`);
  }
  return lines;
}

export function formatDocusignTooltip(summary: LatestDocusignSendSummaryDto): string {
  return getDocusignSummaryLines(summary).join('\n');
}

export function getDocusignSendTargets(
  contract: ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] }
): ContractListDto[] {
  if (contract.isGroupParent && contract.groupContracts?.length) {
    return sortContractsForVtmeGroupOrder(contract.groupContracts);
  }
  return [contract];
}

/** Contratos do grupo que ainda precisam de envio. Grupos com 2+ membros sempre reenviam o grupo inteiro. */
export function getDocusignSendTargetsNeedingSend(
  contract: ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] }
): ContractListDto[] {
  const targets = getDocusignSendTargets(contract);
  if (targets.length <= 1) {
    const state = getPerContractDocusignSendState(targets[0]);
    if (state === 'success' || state === 'processing') {
      return [];
    }
    return targets;
  }

  return targets;
}

export function isPartialGroupDocusignRetry(
  contract: ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] },
  targetsNeedingSend: ContractListDto[]
): boolean {
  const groupMembers = contract.isGroupParent ? contract.groupContracts ?? [] : [];
  return groupMembers.length > 1 && targetsNeedingSend.length > 0 && targetsNeedingSend.length < groupMembers.length;
}

export type DocusignMenuSendScenario =
  | { kind: 'open'; targets: ContractListDto[] }
  | { kind: 'all_sent'; members: ContractListDto[] }
  | {
      kind: 'partial';
      members: ContractListDto[];
      sent: ContractListDto[];
      pending: ContractListDto[];
    }
  | { kind: 'resend_single'; contract: ContractListDto; summary: LatestDocusignSendSummaryDto };

/** Cenário de envio DocuSign ao abrir pelo menu de três pontinhos. */
export function analyzeDocusignMenuSendScenario(
  contract: ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] }
): DocusignMenuSendScenario {
  const isGroup =
    Boolean(contract.isGroupParent) &&
    Boolean(contract.groupContracts?.length && contract.groupContracts.length > 1);

  if (isGroup) {
    const members = getDocusignSendTargets(contract);
    const sent = members.filter((member) => getPerContractDocusignSendState(member) === 'success');
    const pending = members.filter((member) => {
      const state = getPerContractDocusignSendState(member);
      return state === 'none' || state === 'error';
    });

    if (sent.length === members.length) {
      return { kind: 'all_sent', members };
    }
    if (sent.length > 0 && pending.length > 0) {
      return { kind: 'partial', members, sent, pending };
    }
    const targets = getDocusignSendTargetsNeedingSend(contract);
    return { kind: 'open', targets: targets.length > 0 ? targets : members };
  }

  const state = getPerContractDocusignSendState(contract);
  if (state === 'success') {
    return {
      kind: 'resend_single',
      contract,
      summary: contract.latestDocusignSend
        ? normalizeLatestDocusignSendSummary(contract.latestDocusignSend)
        : {
            historyId: '',
            status: EDocusignSendHistoryStatus.Success,
            isProcessing: false,
            statusText: 'Sucesso',
            recipientName: '',
            recipientPhone: '',
            recipientEmail: null,
            recipientContactType: 0,
            recipientContactTypeText: '',
            requestedAt: new Date().toISOString(),
            finishedAt: null,
            docusignAccountEmail: null,
            requestedByUserName: null,
          },
    };
  }

  if (state === 'processing') {
    return { kind: 'open', targets: [] };
  }

  return { kind: 'open', targets: [contract] };
}

/** Alias semântico — mesma lógica usada pelo badge e pelo menu. */
export const analyzeDocusignSendScenario = analyzeDocusignMenuSendScenario;

/** Alvos para reenvio explícito confirmado pelo usuário (inclui contratos já enviados). */
export function getDocusignSendTargetsForResend(
  contract: ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] }
): ContractListDto[] {
  return getDocusignSendTargets(contract);
}
